import React from "react";
import { Box, Typography, Grid, Paper, Chip, Stack } from "@mui/material";
import { useSchemes } from "../../../../contexts/SchemesContext";
import {
  OffensePlayCallLog,
  OffensivePlayLog,
  OffensivePlaySelection,
  PenaltyLog,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { PenaltyLogDetails } from "../Components/PenaltyLogDetails";

interface OffenseLogDetailsProps {
  playCall: OffensePlayCallLog;
  playLog: OffensivePlayLog;
  penalties: PenaltyLog[];
}

export const OffenseLogDetails: React.FC<OffenseLogDetailsProps> = ({
  playCall,
  playLog,
  penalties,
}) => {
  const { offensivePersonnel, formations, formationTags } = useSchemes();

  const Turnover = playLog ? playLog.fumble || playLog.interception : false;

  const formation_tags = playCall?.formation_tags
    ? playCall.formation_tags
        .map((t) => formationTags.find((ft) => ft.id === t)?.name)
        .filter(Boolean)
    : [];

  console.log(playLog);

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2} direction="row">
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {playCall?.personnel_group_name || ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Formation
                </Typography>
                <Typography variant="h6">
                  {playCall?.formation_name}{" "}
                  {playCall?.formation_direction || ""}
                </Typography>
              </Box>

              {formation_tags.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Formation Tags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {formation_tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {playCall?.code_word && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {playCall?.called_pass ? "Pass Pro" : "Run Code Word"}
                  </Typography>
                  <Typography variant="h6">{playCall.code_word}</Typography>
                </Box>
              )}
              {playCall?.pass_concept_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pass Concept
                  </Typography>
                  <Typography variant="h6">
                    {playCall.pass_concept_name}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            <Stack spacing={2} direction="row">
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Gain/Loss
                </Typography>
                <Typography
                  variant="h5"
                  color={
                    playLog && playLog.gain_loss >= 0
                      ? "success.main"
                      : "error.main"
                  }
                >
                  {playLog ? (
                    <>
                      {playLog.gain_loss > 0 ? "+" : ""}
                      {playLog.gain_loss} yards
                    </>
                  ) : (
                    "0 yards"
                  )}
                </Typography>
              </Box>

              {/* Players Involved */}
              {playLog && (
                <Stack spacing={2} direction="row">
                  {playLog.passer_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Passer
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(playLog.passer_roster_player)}
                      </Typography>
                    </Box>
                  )}

                  {playLog.receiver_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Receiver
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(
                          playLog.receiver_roster_player
                        )}
                      </Typography>
                    </Box>
                  )}

                  {playLog.rusher_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Rusher
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(playLog.rusher_roster_player)}
                      </Typography>
                    </Box>
                  )}

                  {/* Turnover Warning */}
                  {Turnover && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                        borderRadius: 1,
                      }}
                    >
                      {playLog.fumble && (
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          FUMBLE
                        </Typography>
                      )}
                      {playLog.interception && (
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          INTERCEPTION
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
        
        {/* Penalties Section */}
        <Grid item xs={12} md={4}>
          <PenaltyLogDetails penalties={penalties} />
        </Grid>
      </Grid>
    </Box>
  );
};
